import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = MONTHS_LONG.map((m) => m.slice(0, 3));
function parts(iso) {
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    return { y, m: m - 1, d };
}
/** "JAN 01 ’27" */
export function tickerDate(iso) {
    const { y, m, d } = parts(iso);
    return `${MONTHS_SHORT[m].toUpperCase()} ${String(d).padStart(2, '0')} ’${String(y).slice(2)}`;
}
/** "January 1, 2027" */
export function longDate(iso) {
    const { y, m, d } = parts(iso);
    return `${MONTHS_LONG[m]} ${d}, ${y}`;
}
/** "Jan 1, 2027" */
export function shortDate(iso) {
    const { y, m, d } = parts(iso);
    return `${MONTHS_SHORT[m]} ${d}, ${y}`;
}
/** The one sentence a screen reader hears for an entry. */
export function entrySpokenText(e, copy) {
    let s = `${e.stateName}: ${e.name} (${e.bill}). `;
    if (e.status === 'in-force' || !e.keyDate)
        return `${s}${copy.inForce}.`;
    s += `${copy.keyDate} ${longDate(e.keyDate)}`;
    s += e.status === 'partly' && e.dutyLabel ? `: ${e.dutyLabel}.` : '.';
    if (e.status === 'partly')
        s += ` ${copy.alreadyInForce}`;
    if (e.note)
        s += ` ${e.note}.`;
    return s;
}
function TickerItems({ entries, copy }) {
    return (_jsx(_Fragment, { children: entries.map((e) => (_jsxs("li", { "data-row": e.row, children: [_jsxs("span", { className: "lw-vis", "aria-hidden": "true", children: [_jsx("span", { className: "lw-j", children: e.state }), _jsx("span", { className: "lw-sep", children: "\u00B7" }), _jsxs("span", { className: "lw-rest", children: [e.status === 'in-force' || !e.keyDate ? (_jsx("span", { className: "lw-inforce", children: copy.inForce })) : (_jsx("time", { dateTime: e.keyDate, children: tickerDate(e.keyDate) })), _jsx("span", { className: "lw-nm", children: e.name }), e.status === 'partly' && e.dutyLabel ? (_jsx("span", { className: "lw-dim", children: `${e.dutyLabel} · ${copy.inForceNow}` })) : null, e.tickerNote ? _jsx("span", { className: "lw-dim", children: `· ${e.tickerNote}` }) : null] })] }), _jsx("span", { className: "sr-only", children: entrySpokenText(e, copy) })] }, e.row))) }));
}
function matches(q) {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
}
/** Duration of one full loop, in seconds (the track holds two copies; it moves -50%). */
const LOOP_SECONDS = 160;
export function LawWatchTicker({ entries, verifiedOn, copy, listId = 'law-watch' }) {
    const [motion, setMotion] = useState(false);
    const [reduced, setReduced] = useState(false);
    const [userPaused, setUserPaused] = useState(false);
    const [hover, setHover] = useState(false);
    const [offscreen, setOffscreen] = useState(false);
    const rootRef = useRef(null);
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const resumeDelay = useRef(undefined);
    useEffect(() => {
        const reduce = matches('(prefers-reduced-motion: reduce)');
        setReduced(reduce);
        setMotion(!reduce);
    }, []);
    useEffect(() => {
        const el = rootRef.current;
        if (!motion || !el || typeof IntersectionObserver === 'undefined')
            return;
        const io = new IntersectionObserver((es) => setOffscreen(!es[0].isIntersecting));
        io.observe(el);
        return () => io.disconnect();
    }, [motion]);
    // A user pause turns the strip into a sideways scroller, starting where the motion stopped;
    // resuming continues the loop from wherever the user scrolled to.
    const scrub = motion && userPaused;
    useEffect(() => {
        const vp = viewportRef.current;
        const track = trackRef.current;
        if (!vp || !track || !motion)
            return;
        const half = track.scrollWidth / 2;
        if (scrub) {
            const t = getComputedStyle(track).transform;
            const m = /matrix\(([^)]+)\)/.exec(t);
            const x = m ? parseFloat(m[1].split(',')[4]) : 0;
            track.dataset.scrub = '';
            vp.scrollLeft = Math.max(0, -x);
        }
        else if ('scrub' in track.dataset) {
            const offset = half > 0 ? (vp.scrollLeft % half) / half : 0;
            resumeDelay.current = `-${(offset * LOOP_SECONDS).toFixed(2)}s`;
            delete track.dataset.scrub;
            vp.scrollLeft = 0;
            track.style.animationDelay = resumeDelay.current;
        }
    }, [scrub, motion]);
    const paused = userPaused || hover || offscreen;
    const pressed = userPaused || hover;
    const onPointerEnter = useCallback((e) => { if (e.pointerType === 'mouse')
        setHover(true); }, []);
    const onPointerLeave = useCallback((e) => { if (e.pointerType === 'mouse')
        setHover(false); }, []);
    // Touch / pen: a tap toggles the pause (decided per event, so a tablet that gains a trackpad still works).
    // A swipe on the paused strip scrolls it; only a tap (little movement) toggles.
    const downAt = useRef(null);
    const onPointerDown = useCallback((e) => { downAt.current = { x: e.clientX, y: e.clientY }; }, []);
    const onPointerUp = useCallback((e) => {
        const d = downAt.current;
        downAt.current = null;
        if (e.pointerType === 'mouse' || !d)
            return;
        if (Math.abs(e.clientX - d.x) > 10 || Math.abs(e.clientY - d.y) > 10)
            return;
        setUserPaused((p) => !p);
    }, []);
    const toList = () => {
        window.setTimeout(() => document.getElementById(listId)?.focus({ preventScroll: true }), 0);
    };
    const strip = reduced || scrub;
    const v = parts(verifiedOn);
    return (_jsxs("section", { ref: rootRef, className: "lw-ticker", "data-law-ticker": true, "data-ready": motion || undefined, "data-paused": (motion && paused) || undefined, "aria-label": `${copy.regionLabel}, ${copy.verified.toLowerCase()} ${longDate(verifiedOn)}`, children: [_jsxs("div", { className: "lw-label", children: [_jsxs("div", { className: "lw-id", children: [_jsx("span", { className: "lw-title", "aria-hidden": "true", children: copy.title }), _jsxs("span", { className: "lw-meta", children: [_jsx("span", { className: "lw-sep lw-narrow", "aria-hidden": "true", children: "\u00B7" }), _jsxs("span", { className: "lw-stamp", children: [_jsxs("span", { "aria-hidden": "true", children: [`${copy.verified} ${MONTHS_SHORT[v.m]} ${v.d}`, _jsx("span", { className: "lw-yr", children: `, ${v.y}` })] }), _jsx("span", { className: "sr-only", children: `${copy.verified} ${MONTHS_SHORT[v.m]} ${v.d}, ${v.y}` })] }), _jsx("span", { className: "lw-sep lw-narrow", "aria-hidden": "true", children: "\u00B7" }), _jsxs("a", { className: "lw-all", href: `#${listId}`, onClick: toList, children: [`${copy.showAll} ${entries.length}`, _jsx("span", { className: "lw-long", children: ` ${copy.showAllLong}` }), _jsx("span", { className: "lw-arrow", "aria-hidden": "true", children: ' ↓' })] })] })] }), motion ? (_jsx("button", { className: "lw-pause", type: "button", "aria-pressed": pressed, "aria-label": copy.pause, onClick: () => { setUserPaused((p) => !p); setHover(false); }, children: pressed ? (_jsx("svg", { viewBox: "0 0 14 14", "aria-hidden": "true", children: _jsx("path", { d: "M3 1l10 6-10 6z", fill: "currentColor" }) })) : (_jsxs("svg", { viewBox: "0 0 14 14", "aria-hidden": "true", children: [_jsx("rect", { x: "2", y: "1", width: "3.5", height: "12", fill: "currentColor" }), _jsx("rect", { x: "8.5", y: "1", width: "3.5", height: "12", fill: "currentColor" })] })) })) : null] }), _jsx("div", { ref: viewportRef, className: "lw-viewport", "data-strip": strip || undefined, onPointerEnter: motion ? onPointerEnter : undefined, onPointerLeave: motion ? onPointerLeave : undefined, onPointerDown: motion ? onPointerDown : undefined, onPointerUp: motion ? onPointerUp : undefined, 
                // The sideways strip is reachable by keyboard; the moving strip is not a tab stop.
                tabIndex: strip ? 0 : undefined, role: strip ? 'group' : undefined, "aria-label": strip ? copy.stripLabel : undefined, children: _jsxs("div", { ref: trackRef, className: "lw-track", children: [_jsx("ul", { className: "lw-list", children: _jsx(TickerItems, { entries: entries, copy: copy }) }), motion ? (_jsx("ul", { className: "lw-list", "aria-hidden": "true", inert: '', "data-dup": true, children: _jsx(TickerItems, { entries: entries, copy: copy }) })) : null] }) })] }));
}
export function groupLawWatch(entries, copy) {
    const now = [];
    const byDate = new Map();
    for (const e of entries) {
        if (e.status === 'in-force' || !e.keyDate) {
            now.push(e);
            continue;
        }
        const list = byDate.get(e.keyDate) ?? [];
        list.push(e);
        byDate.set(e.keyDate, list);
    }
    const groups = [];
    if (now.length)
        groups.push({ key: 'now', heading: copy.listGroupNow, now: true, items: now });
    for (const date of [...byDate.keys()].sort()) {
        const items = byDate.get(date);
        groups.push({ key: date, heading: `${shortDate(date)} · ${items.length} ${items.length === 1 ? copy.law : copy.laws}`, now: false, items });
    }
    return groups;
}
function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
export function LawWatchList({ entries, verifiedOn, copy, id = 'law-watch', cta }) {
    const titleId = `${id}-title`;
    return (_jsxs("div", { className: "lw-static", "data-law-list": true, id: id, tabIndex: -1, "aria-labelledby": titleId, children: [_jsxs("div", { className: "lw-static-head", children: [_jsx("h3", { id: titleId, children: `${copy.listHeading}: ${entries.length} ${copy.showAllLong}` }), _jsx("span", { className: "lw-static-stamp", children: `${copy.verified} ${shortDate(verifiedOn)}` })] }), _jsx("div", { className: "lw-groups", children: groupLawWatch(entries, copy).map((g) => (_jsxs("div", { className: "lw-group", children: [_jsx("h4", { "data-now": g.now || undefined, children: g.heading }), _jsx("ul", { children: g.items.map((e) => (_jsxs("li", { "data-row": e.row, children: [_jsx("span", { className: "lw-j", "aria-hidden": "true", children: e.state }), _jsx("span", { className: "sr-only", children: `${e.stateName}: ` }), e.name, _jsx("span", { className: "lw-bill", children: e.bill }), e.status === 'partly' && e.dutyLabel ? (_jsx("span", { className: "lw-later", children: `${capitalize(e.dutyLabel)} ${copy.bindAlready}` })) : null, e.note ? _jsx("span", { className: "lw-later", children: e.note }) : null] }, e.row))) })] }, g.key))) }), _jsx("p", { className: "lw-src", children: copy.sourceNote }), cta ? _jsxs("p", { className: "lw-end", children: [copy.listCtaLead, " ", cta] }) : null] }));
}
//# sourceMappingURL=LawWatch.js.map